// Thin client for the integration_hub backend. Explicit method per endpoint,
// throw on non-2xx with the response body as the message. Every request is sent
// through the chatbot_sso SDK's authenticated fetch (Bearer access_token +
// 401-refresh) when available — see authBridge. X-API-Key is kept as a
// secondary credential for the hub's `key|token` routes.
import { config } from '../config';
import { getAuthedFetch } from './authBridge';
import { mergeRequestHeaders } from '../lib/requestHeaders';
import { instructionsPath, readinessPath } from '../lib/assistantView';
import type { BotRef } from '../lib/botNav';
import { conversionsPath, convertPath, type Conversions, type ConvertResult, correctionsPath, type Correction, type CorrectionChange, type CorrectionEvaluation, type TextEvalCase, type TextEvalRun } from '../lib/latinView';

import type { WhyTrace } from '../lib/blockWhy';
import type { LinkStat } from '../lib/blockLinksView';
import { reviewQuery, type Metrics, type ReviewFilters, type ReviewGroups, type ReviewStatus } from '../lib/answerReviewView';
import {
  restorePath,
  versionPath,
  versionsPath,
  type InstructionVersion,
  type InstructionVersionSummary,
} from '../lib/instructionsHistoryView';
import {
  testerPath,
  type AcceptResult,
  type Estimate,
  type Freshness,
  type QuestionPage,
  type Run,
  type RunItem,
  type LinkOption,
  type Suggestion,
  type SuggestionStatus,
  type SuggestOutcome,
  type TesterQuestion,
  type UndoResult,
  type Verdict,
} from '../lib/testerView';

/** A correction as saved, with what it did to the checked conversions (P3). */
export type Evaluated = Correction & { evaluation?: CorrectionEvaluation | null };

// ── Forms domain types (match backend/src/form) ──────────────────────────────

export type FormFieldType = 'input' | 'select' | 'file' | 'confirm';

/** Exactly one of these is conventionally set. */
export interface FormCompletion {
  tool?: string;
  workflowId?: string;
  /** Static confirmation utterance; supports {{slot}} interpolation. */
  utter?: string;
}

export interface Form {
  id: string;
  system: string;
  botId: string;
  name: string;
  completion: FormCompletion | null;
  /** 'A' active | 'D' soft-deleted. */
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface FormFieldValue {
  id: string;
  fieldId: string;
  label: string;
  value: string;
  /** FormField ids to skip if this option is chosen (branching). */
  hide?: string[] | null;
  ordering: number;
}

export interface FormField {
  id: string;
  formId: string;
  name: string;
  question: string;
  type: FormFieldType;
  regex?: string | null;
  invalidReply?: string | null;
  isRequired: boolean;
  ordering: number;
  tag?: string | null;
  maxRetries?: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface FormFieldWithValues extends FormField {
  values: FormFieldValue[];
}

export interface FormDetail {
  form: Form;
  fields: FormFieldWithValues[];
}

// ── DTOs ─────────────────────────────────────────────────────────────────────

export interface CreateFormDto {
  system: string;
  botId: string;
  name: string;
  completion?: FormCompletion;
}

export interface UpdateFormDto {
  name?: string;
  completion?: FormCompletion;
  status?: string;
}

export interface FormFieldValueDto {
  label: string;
  value: string;
  hide?: string[];
  ordering?: number;
}

export interface CreateFormFieldDto {
  name: string;
  question: string;
  type?: FormFieldType;
  regex?: string;
  invalidReply?: string;
  isRequired?: boolean;
  ordering?: number;
  tag?: string;
  maxRetries?: number;
  /** Options for select fields; replaces all options wholesale. */
  values?: FormFieldValueDto[];
}

export type UpdateFormFieldDto = Partial<CreateFormFieldDto>;

// ── Memory domain types (match backend/src/memory) ───────────────────────────

/** Tenant-scoped config row (key/value per tenantId). */
export interface TenantConfig {
  id: string;
  tenantId: string;
  key: string;
  value: unknown;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Long-term per-end-user profile, keyed by (system, psid). */
export interface UserProfile {
  id: string;
  system: string;
  psid: string;
  pageId?: string | null;
  attrs?: Record<string, unknown> | null;
  summary?: string | null;
  lastSeen?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserProfileDto {
  system: string;
  psid: string;
  pageId?: string;
  attrs?: Record<string, unknown>;
  summary?: string;
}

export interface UpdateUserProfileDto {
  attrs?: Record<string, unknown>;
  summary?: string;
  lastSeen?: string;
}

/** Conventional tenant-config keys (free-form; these are just suggestions). */
export const SUGGESTED_TENANT_KEYS = [
  'tone',
  'fallback_behavior',
  'system_prompt_overlay',
  'compliance.disclaimers',
  'tools.enabled',
  'tools.disabled',
] as const;

// ── transport ────────────────────────────────────────────────────────────────

async function hubFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const url = `${config.hubServer}${path.startsWith('/') ? path : `/${path}`}`;
  const headers = mergeRequestHeaders(
    { 'Content-Type': 'application/json' }, // Bearer JWT only (no X-API-Key since 2026-07-03)
    init.headers,
  );
  const doFetch = getAuthedFetch() ?? fetch;
  return doFetch(url, { ...init, headers });
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    if (res.status === 403) {
      throw new Error("403 You don't have access to this bot or page. Ask the bot's owner to add you in SSO.");
    }
    let detail = '';
    try {
      detail = await res.text();
    } catch {
      /* ignore */
    }
    throw new Error(`${res.status} ${res.statusText}${detail ? ` — ${detail}` : ''}`);
  }
  // 204 No Content (DELETE)
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v != null && v !== '') sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

// ── Forms API ─────────────────────────────────────────────────────────────────

/** Counts of what an import would do (POST /api/intent-import/:system/:botId/preview). */
export interface ImportPreview {
  intents: number;
  general: number;
  bot: number;
  skippedControl: number;
  skippedNoPhrases: number;
  weak: number;
  phrases: number;
  duplicateNames: string[];
}

/** One imported bot (GET /api/intent-import). */
export interface IntentImportRow {
  id: string;
  system: string;
  botId: string;
  botName: string | null;
  state: 'importing' | 'shadow' | 'live';
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  summary: ImportPreview | null;
  processed: number;
  total: number;
  failed: number;
  error: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  lastSyncedAt: string | null;
  updatedAt: string;
}

export type AuthzMode = 'off' | 'shadow' | 'enforce';

/** GET /api/me — who the caller is (authz spec §7). */
export type Me =
  | {
      kind: 'operator';
      userId: string;
      isGlobalAdmin: boolean;
      globalRoles: string[];
      authzMode: AuthzMode;
      rolesUnavailable?: boolean;
    }
  | { kind: 'service'; authzMode: AuthzMode };

/** GET /api/me/bots — bots the caller can open (hub-ui-step1 spec §3). `botId` is hub's key. */
export interface HubBot {
  system: 'chatbot' | 'chatbot_v2';
  botId: string;
  botNo: string;
  name: string | null;
  role: 'owner' | 'admin' | 'member' | 'viewer' | null;
  tenantName: string | null;
}
export interface MyBots {
  scope: 'mine' | 'all';
  bots: HubBot[];
  /** v1 bots you can open whose identity has not reached hub yet (bot-identity-sync). */
  unsynced: number;
}

/** A2 step 1: a bot's instructions (tool-service static context, fronted by the hub). */
export interface BotInstructions {
  system: string;
  botId: string;
  configured: boolean;
  active: boolean | null;
  content: string;
}

/** Bot routing R1: which engines answer the bot (hub record; defaults overlaid). */
export interface BotRouting {
  system: string;
  botId: string;
  flow: Record<string, string>;
  stored: boolean;
  updatedAt: string | null;
}
export interface BotRoutingChange {
  id: string;
  actor: string | null;
  changes: Record<string, [string, string]>;
  createdAt: string;
}

/** GET /api/bot-readiness/:system/:botId — derived on every read, never stored. */
export interface BotReadiness {
  system: string;
  botId: string;
  ready: 'ready' | 'not_ready' | 'unknown';
  checks: { key: string; status: 'ok' | 'failed' | 'unknown'; detail: string }[];
}

export const hubClient = {
  me(): Promise<Me> {
    return hubFetch('/api/me').then((r) => json<Me>(r));
  },
  getBotInstructions(bot: BotRef): Promise<BotInstructions> {
    return hubFetch(instructionsPath(bot)).then((r) => json<BotInstructions>(r));
  },
  putBotInstructions(bot: BotRef, content: string): Promise<BotInstructions> {
    return hubFetch(instructionsPath(bot), { method: 'PUT', body: JSON.stringify({ content }) }).then((r) =>
      json<BotInstructions>(r),
    );
  },
  deleteBotInstructions(bot: BotRef): Promise<{ removed: true }> {
    return hubFetch(instructionsPath(bot), { method: 'DELETE' }).then((r) => json<{ removed: true }>(r));
  },
  listInstructionVersions(bot: BotRef): Promise<{ versions: InstructionVersionSummary[] }> {
    return hubFetch(versionsPath(bot)).then((r) => json<{ versions: InstructionVersionSummary[] }>(r));
  },
  getInstructionVersion(bot: BotRef, id: string): Promise<InstructionVersion> {
    return hubFetch(versionPath(bot, id)).then((r) => json<InstructionVersion>(r));
  },
  restoreInstructionVersion(bot: BotRef, id: string): Promise<BotInstructions> {
    return hubFetch(restorePath(bot, id), { method: 'POST' }).then((r) => json<BotInstructions>(r));
  },
  /** Knowledge › Blocks "Recent customer questions": the bot's latest answers that carry a block decision. */
  recentBlockTraces(bot: BotRef, limit = 50, offset = 0, channel?: string): Promise<{ items: WhyTrace[]; total: number }> {
    const q = qs({ withBlock: 'true', limit: String(limit), offset: String(offset), channel });
    return hubFetch(`/api/answer-traces/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}${q}`).then(
      (r) => json<{ items: WhyTrace[]; total: number }>(r),
    );
  },
  /** Knowledge › Blocks cards: per link, closest / sent counts over the last `days`. */
  blockLinkStats(bot: BotRef, days = 7, channel?: string): Promise<{ days: number; decisions: number; links: Record<string, LinkStat> }> {
    const q = qs({ days: String(days), channel });
    return hubFetch(`/api/answer-traces/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/block-stats${q}`).then(
      (r) => json<{ days: number; decisions: number; links: Record<string, LinkStat> }>(r),
    );
  },
  answerReviewGroups(bot: BotRef, f: ReviewFilters): Promise<ReviewGroups> {
    return hubFetch(`/api/answer-review/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}${qs(reviewQuery(f))}`).then((r) =>
      json<ReviewGroups>(r),
    );
  },
  /** Quality › Latin text (text conversion P1): changed words in the window, most frequent first. */
  textConversions(bot: BotRef, days: number): Promise<Conversions> {
    return hubFetch(conversionsPath(bot, days)).then((r) => json<Conversions>(r));
  },
  /** Quality › Latin text › Test: what the converter makes of a text for this bot now. */
  textConvert(bot: BotRef, text: string): Promise<ConvertResult> {
    return hubFetch(convertPath(bot), { method: 'POST', body: JSON.stringify({ text }) }).then((r) => json<ConvertResult>(r));
  },
  /** Quality › Latin text › Corrections (P2): this bot's rules and the all-bots rules that apply. */
  textCorrections(bot: BotRef): Promise<{ bot: Correction[]; global: Correction[] }> {
    return hubFetch(correctionsPath(bot)).then((r) => json<{ bot: Correction[]; global: Correction[] }>(r));
  },
  /** P3: the result carries `evaluation` (checked conversions before → after; null = could not check). */
  textCorrectionCreate(bot: BotRef, body: { latin: string; cyrillic: string; note?: string; suggestGlobal?: boolean }): Promise<Evaluated> {
    return hubFetch(correctionsPath(bot), { method: 'POST', body: JSON.stringify(body) }).then((r) => json<Evaluated>(r));
  },
  textCorrectionUpdate(bot: BotRef, id: string, body: { cyrillic?: string; enabled?: boolean; note?: string; suggestGlobal?: boolean }): Promise<Evaluated> {
    return hubFetch(correctionsPath(bot, id), { method: 'PATCH', body: JSON.stringify(body) }).then((r) => json<Evaluated>(r));
  },
  textCorrectionChanges(bot: BotRef, id: string): Promise<CorrectionChange[]> {
    return hubFetch(`${correctionsPath(bot, id)}/changes`).then((r) => json<CorrectionChange[]>(r));
  },
  /** Platform › Text conversion (admins): all-bots rules and the "suggested for all bots" queue. */
  globalCorrections(): Promise<Correction[]> {
    return hubFetch('/api/text-corrections/global').then((r) => json<Correction[]>(r));
  },
  /** All-bots writes: a 409 with `needsConfirm` (see latinView.needsConfirm) is resent with `confirm: true`. */
  globalCorrectionCreate(body: { latin: string; cyrillic: string; note?: string; confirm?: boolean }): Promise<Evaluated> {
    return hubFetch('/api/text-corrections/global', { method: 'POST', body: JSON.stringify(body) }).then((r) => json<Evaluated>(r));
  },
  globalCorrectionUpdate(id: string, body: { cyrillic?: string; enabled?: boolean; note?: string; confirm?: boolean }): Promise<Evaluated> {
    return hubFetch(`/api/text-corrections/global/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) =>
      json<Evaluated>(r),
    );
  },
  globalCorrectionChanges(id: string): Promise<CorrectionChange[]> {
    return hubFetch(`/api/text-corrections/global/${encodeURIComponent(id)}/changes`).then((r) => json<CorrectionChange[]>(r));
  },
  correctionSuggestions(): Promise<Correction[]> {
    return hubFetch('/api/text-corrections/suggestions').then((r) => json<Correction[]>(r));
  },
  correctionApprove(id: string, cyrillic?: string, confirm?: boolean): Promise<Evaluated> {
    return hubFetch(`/api/text-corrections/suggestions/${encodeURIComponent(id)}/approve`, {
      method: 'POST',
      body: JSON.stringify({ ...(cyrillic ? { cyrillic } : {}), ...(confirm ? { confirm: true } : {}) }),
    }).then((r) => json<Evaluated>(r));
  },
  correctionReject(id: string): Promise<Correction> {
    return hubFetch(`/api/text-corrections/suggestions/${encodeURIComponent(id)}/reject`, { method: 'POST', body: '{}' }).then((r) =>
      json<Correction>(r),
    );
  },
  /** P3 checked conversions: *Looks right* (this bot), and the admin's cases / seed / runs. */
  textEvalConfirm(bot: BotRef, body: { latin: string; expected: string }): Promise<TextEvalCase> {
    return hubFetch(`/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/eval-cases`, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => json<TextEvalCase>(r));
  },
  textEvalCases(bot: BotRef): Promise<TextEvalCase[]> {
    return hubFetch(`/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/eval-cases`).then((r) => json<TextEvalCase[]>(r));
  },
  textEvalDelete(bot: BotRef, id: string): Promise<void> {
    return hubFetch(`/api/text/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/eval-cases/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }).then((r) => json<void>(r));
  },
  evalCases(): Promise<TextEvalCase[]> {
    return hubFetch('/api/text-eval/cases').then((r) => json<TextEvalCase[]>(r));
  },
  evalCaseDelete(id: string): Promise<void> {
    return hubFetch(`/api/text-eval/cases/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => json<void>(r));
  },
  evalSeed(body: { system?: string; botId?: string }): Promise<{ added: number; skipped: number }> {
    return hubFetch('/api/text-eval/seed', { method: 'POST', body: JSON.stringify(body) }).then((r) => json<{ added: number; skipped: number }>(r));
  },
  evalRun(): Promise<TextEvalRun> {
    return hubFetch('/api/text-eval/runs', { method: 'POST', body: '{}' }).then((r) => json<TextEvalRun>(r));
  },
  evalRuns(limit = 30): Promise<TextEvalRun[]> {
    return hubFetch(`/api/text-eval/runs${qs({ limit: String(limit) })}`).then((r) => json<TextEvalRun[]>(r));
  },
  answerReviewMetrics(bot: BotRef, days: number): Promise<Metrics> {
    return hubFetch(`/api/answer-review/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/metrics${qs({ days: String(days) })}`).then(
      (r) => json<Metrics>(r),
    );
  },
  answerReviewDecide(bot: BotRef, body: { traceIds: string[]; status: ReviewStatus; note?: string }): Promise<{ updated: number; status: string }> {
    return hubFetch(`/api/answer-review/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/decisions`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }).then((r) => json<{ updated: number; status: string }>(r));
  },
  // Quality › Tests and Suggestions (spec 2026-10-12 §4.3–4.4): /api/tester/:system/:botId/*, bot-scoped.
  testerQuestions(bot: BotRef, q: { status?: 'active' | 'stale'; limit?: number; offset?: number } = {}): Promise<QuestionPage> {
    const query = qs({ status: q.status, limit: q.limit?.toString(), offset: q.offset?.toString() });
    return hubFetch(testerPath(bot, `questions${query}`)).then((r) => json<QuestionPage>(r));
  },
  testerUpdateQuestion(bot: BotRef, id: number, patch: { question?: string; status?: 'active' | 'stale'; expectedBlock?: string | null }): Promise<TesterQuestion> {
    return hubFetch(testerPath(bot, `questions/${id}`), { method: 'PATCH', body: JSON.stringify(patch) }).then((r) => json<TesterQuestion>(r));
  },
  /** The bot's block links (Knowledge › Blocks) for the expected-block picker and the run's block titles. */
  testerBlockLinks(bot: BotRef): Promise<LinkOption[]> {
    return hubFetch(`/api/knowledge/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/block-links`)
      .then((r) => json<{ links: LinkOption[] }>(r))
      .then((r) => r.links ?? []);
  },
  testerDeleteQuestion(bot: BotRef, id: number): Promise<{ deleted: number }> {
    return hubFetch(testerPath(bot, `questions/${id}`), { method: 'DELETE' }).then((r) => json<{ deleted: number }>(r));
  },
  testerDeleteQuestions(bot: BotRef): Promise<{ deleted: number }> {
    return hubFetch(testerPath(bot, 'questions'), { method: 'DELETE' }).then((r) => json<{ deleted: number }>(r));
  },
  testerFreshness(bot: BotRef): Promise<Freshness> {
    return hubFetch(testerPath(bot, 'freshness')).then((r) => json<Freshness>(r));
  },
  testerGenerate(bot: BotRef, perChunk: number): Promise<{ jobId: string; status: string }> {
    return hubFetch(testerPath(bot, 'questions/generate'), { method: 'POST', body: JSON.stringify({ perChunk }) }).then((r) =>
      json<{ jobId: string; status: string }>(r),
    );
  },
  testerEstimate(bot: BotRef): Promise<Estimate> {
    return hubFetch(testerPath(bot, 'runs/estimate')).then((r) => json<Estimate>(r));
  },
  testerStartRun(bot: BotRef, maxQuestions?: number): Promise<{ jobId: string; questionCount: number; capped: boolean }> {
    return hubFetch(testerPath(bot, 'runs'), { method: 'POST', body: JSON.stringify(maxQuestions ? { maxQuestions } : {}) }).then((r) =>
      json<{ jobId: string; questionCount: number; capped: boolean }>(r),
    );
  },
  testerRuns(bot: BotRef, offset = 0): Promise<{ items: Run[]; total: number }> {
    return hubFetch(testerPath(bot, `runs${qs({ limit: '20', offset: String(offset) })}`)).then((r) => json<{ items: Run[]; total: number }>(r));
  },
  testerRun(bot: BotRef, jobId: string): Promise<Run> {
    return hubFetch(testerPath(bot, `runs/${encodeURIComponent(jobId)}`)).then((r) => json<Run>(r));
  },
  testerRunItems(bot: BotRef, jobId: string, verdict?: Verdict): Promise<{ items: RunItem[]; total: number }> {
    return hubFetch(testerPath(bot, `runs/${encodeURIComponent(jobId)}/items${qs({ verdict })}`)).then((r) =>
      json<{ items: RunItem[]; total: number }>(r),
    );
  },
  testerCancelRun(bot: BotRef, jobId: string): Promise<{ jobId: string; cancelRequested: boolean; cancelled: boolean }> {
    return hubFetch(testerPath(bot, `runs/${encodeURIComponent(jobId)}/cancel`), { method: 'POST' }).then((r) =>
      json<{ jobId: string; cancelRequested: boolean; cancelled: boolean }>(r),
    );
  },
  testerSuggest(bot: BotRef, jobId: string): Promise<SuggestOutcome> {
    return hubFetch(testerPath(bot, `runs/${encodeURIComponent(jobId)}/suggest`), { method: 'POST' }).then((r) => json<SuggestOutcome>(r));
  },
  testerSuggestions(bot: BotRef, status: SuggestionStatus): Promise<{ items: Suggestion[]; count: number }> {
    return hubFetch(testerPath(bot, `suggestions${qs({ status })}`)).then((r) => json<{ items: Suggestion[]; count: number }>(r));
  },
  testerAccept(bot: BotRef, ids: string[]): Promise<{ results: AcceptResult[]; accepted: number }> {
    return hubFetch(testerPath(bot, 'suggestions/accept'), { method: 'POST', body: JSON.stringify({ ids }) }).then((r) =>
      json<{ results: AcceptResult[]; accepted: number }>(r),
    );
  },
  testerReject(bot: BotRef, ids: string[]): Promise<{ rejected: number; ids: string[]; skipped: string[] }> {
    return hubFetch(testerPath(bot, 'suggestions/reject'), { method: 'POST', body: JSON.stringify({ ids }) }).then((r) =>
      json<{ rejected: number; ids: string[]; skipped: string[] }>(r),
    );
  },
  testerUndo(bot: BotRef, id: string): Promise<UndoResult> {
    return hubFetch(testerPath(bot, `suggestions/${encodeURIComponent(id)}/undo`), { method: 'POST' }).then((r) => json<UndoResult>(r));
  },
  getBotRouting(bot: BotRef): Promise<BotRouting> {
    return hubFetch(`/api/bot-routing/${bot.system}/${encodeURIComponent(bot.botId)}`).then((r) => json<BotRouting>(r));
  },
  changeBotRouting(bot: BotRef, field: string, value: string): Promise<BotRouting> {
    return hubFetch(`/api/bot-routing/${bot.system}/${encodeURIComponent(bot.botId)}`, {
      method: 'PUT',
      body: JSON.stringify({ field, value }),
    }).then((r) => json<BotRouting>(r));
  },
  getBotRoutingHistory(bot: BotRef, limit = 5): Promise<BotRoutingChange[]> {
    return hubFetch(`/api/bot-routing/${bot.system}/${encodeURIComponent(bot.botId)}/history?limit=${limit}`).then((r) =>
      json<BotRoutingChange[]>(r),
    );
  },
  getBotReadiness(bot: BotRef): Promise<BotReadiness> {
    return hubFetch(readinessPath(bot)).then((r) => json<BotReadiness>(r));
  },

  myBots(q = ''): Promise<MyBots> {
    return hubFetch(`/api/me/bots${qs({ q: q.trim() || undefined })}`).then((r) => json<MyBots>(r));
  },

  /** Platform: intent import waves (NLU P1 E10). Services and SSO global admins only. */
  intentImport: {
    list: () => hubFetch('/api/intent-import').then((r) => json<IntentImportRow[]>(r)),
    preview: (system: string, botId: string) =>
      hubFetch(`/api/intent-import/${encodeURIComponent(system)}/${encodeURIComponent(botId)}/preview`, { method: 'POST' }).then((r) =>
        json<ImportPreview>(r),
      ),
    start: (system: string, botId: string) =>
      hubFetch(`/api/intent-import/${encodeURIComponent(system)}/${encodeURIComponent(botId)}/start`, { method: 'POST' }).then((r) =>
        json<IntentImportRow>(r),
      ),
    startBulk: (system: string, botIds: string[]) =>
      hubFetch('/api/intent-import/bulk/start', { method: 'POST', body: JSON.stringify({ system, botIds }) }).then((r) =>
        json<{ queued: number }>(r),
      ),
    setState: (system: string, botId: string, state: 'shadow' | 'live') =>
      hubFetch(`/api/intent-import/${encodeURIComponent(system)}/${encodeURIComponent(botId)}/state`, {
        method: 'PUT',
        body: JSON.stringify({ state }),
      }).then((r) => json<IntentImportRow>(r)),
    remove: (system: string, botId: string) =>
      hubFetch(`/api/intent-import/${encodeURIComponent(system)}/${encodeURIComponent(botId)}`, { method: 'DELETE' }).then((r) =>
        json<void>(r),
      ),
  },


  listForms(system: string, botId: string): Promise<Form[]> {
    return hubFetch(`/api/forms${qs({ system, botId })}`).then((r) => json<Form[]>(r));
  },

  getForm(id: string): Promise<FormDetail> {
    return hubFetch(`/api/forms/${encodeURIComponent(id)}`).then((r) => json<FormDetail>(r));
  },

  createForm(dto: CreateFormDto): Promise<Form> {
    return hubFetch('/api/forms', { method: 'POST', body: JSON.stringify(dto) }).then((r) =>
      json<Form>(r),
    );
  },

  updateForm(id: string, dto: UpdateFormDto): Promise<Form> {
    return hubFetch(`/api/forms/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }).then((r) => json<Form>(r));
  },

  deleteForm(id: string): Promise<void> {
    return hubFetch(`/api/forms/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) =>
      json<void>(r),
    );
  },

  createField(formId: string, dto: CreateFormFieldDto): Promise<FormField> {
    return hubFetch(`/api/forms/${encodeURIComponent(formId)}/fields`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }).then((r) => json<FormField>(r));
  },

  updateField(fieldId: string, dto: UpdateFormFieldDto): Promise<FormField> {
    return hubFetch(`/api/forms/fields/${encodeURIComponent(fieldId)}`, {
      method: 'PUT',
      body: JSON.stringify(dto),
    }).then((r) => json<FormField>(r));
  },

  deleteField(fieldId: string): Promise<void> {
    return hubFetch(`/api/forms/fields/${encodeURIComponent(fieldId)}`, {
      method: 'DELETE',
    }).then((r) => json<void>(r));
  },

  // ── Memory: tenant config (jwt_only — works with the SSO user token) ─────────

  /** Resolve a bot's tenantId via the mappings route; null if unmapped (404). */
  async resolveTenantId(system: string, botId: string): Promise<string | null> {
    const r = await hubFetch(`/api/bots/mappings/resolve/${encodeURIComponent(botId)}${qs({ system })}`);
    if (!r.ok) return null;
    const body = (await r.json()) as { tenantId?: string; tenant_id?: string } | string | null;
    if (typeof body === 'string') return body || null;
    return body?.tenantId ?? body?.tenant_id ?? null;
  },

  listTenantConfig(tenantId: string): Promise<TenantConfig[]> {
    return hubFetch(`/api/tenant-config/tenants/${encodeURIComponent(tenantId)}`).then((r) =>
      json<TenantConfig[]>(r),
    );
  },

  putTenantConfig(
    tenantId: string,
    key: string,
    body: { value: unknown; description?: string },
  ): Promise<TenantConfig> {
    return hubFetch(
      `/api/tenant-config/tenants/${encodeURIComponent(tenantId)}/keys/${encodeURIComponent(key)}`,
      { method: 'PUT', body: JSON.stringify(body) },
    ).then((r) => json<TenantConfig>(r));
  },

  deleteTenantConfig(tenantId: string, key: string): Promise<void> {
    return hubFetch(
      `/api/tenant-config/tenants/${encodeURIComponent(tenantId)}/keys/${encodeURIComponent(key)}`,
      { method: 'DELETE' },
    ).then((r) => json<void>(r));
  },

  // ── Memory: user profile (keyed by system + psid) ────────────────────────────

  /** Returns null when no profile exists for (system, psid) (404). */
  async getUserProfile(system: string, psid: string): Promise<UserProfile | null> {
    const r = await hubFetch(`/api/user-profile${qs({ system, psid })}`);
    if (r.status === 404) return null;
    return json<UserProfile>(r);
  },

  createUserProfile(dto: CreateUserProfileDto): Promise<UserProfile> {
    return hubFetch('/api/user-profile', { method: 'POST', body: JSON.stringify(dto) }).then((r) =>
      json<UserProfile>(r),
    );
  },

  updateUserProfile(id: string, dto: UpdateUserProfileDto): Promise<UserProfile> {
    return hubFetch(`/api/user-profile/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }).then((r) => json<UserProfile>(r));
  },

  deleteUserProfile(id: string): Promise<void> {
    return hubFetch(`/api/user-profile/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) =>
      json<void>(r),
    );
  },

  // ── Session events (runtime trace log; @IamOnly → SSO token) ──────────────────
  listSessionEvents(p: { system?: string; botId?: string; kind?: string; limit?: number }): Promise<SessionEvent[]> {
    return hubFetch(
      `/api/session-events${qs({ system: p.system, botId: p.botId, kind: p.kind, limit: p.limit != null ? String(p.limit) : undefined })}`,
    ).then((r) => json<SessionEvent[]>(r));
  },
  getSessionEventsBySession(sessionId: string, limit?: number): Promise<SessionEvent[]> {
    return hubFetch(
      `/api/session-events/by-session/${encodeURIComponent(sessionId)}${qs({ limit: limit != null ? String(limit) : undefined })}`,
    ).then((r) => json<SessionEvent[]>(r));
  },

  // ── Hooks (lifecycle hooks; key|token) ───────────────────────────────────────
  listHooks(system: string, botId: string, event?: string): Promise<Hook[]> {
    return hubFetch(`/api/hooks${qs({ system, botId, event })}`).then((r) => json<Hook[]>(r));
  },
  getHookMeta(): Promise<HookMeta> {
    return hubFetch('/api/hooks/meta').then((r) => json<HookMeta>(r));
  },
  createHook(dto: CreateHookDto): Promise<Hook> {
    return hubFetch('/api/hooks', { method: 'POST', body: JSON.stringify(dto) }).then((r) => json<Hook>(r));
  },
  updateHook(id: string, dto: UpdateHookDto): Promise<Hook> {
    return hubFetch(`/api/hooks/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(dto) }).then((r) =>
      json<Hook>(r),
    );
  },
  deleteHook(id: string): Promise<void> {
    return hubFetch(`/api/hooks/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => json<void>(r));
  },

  // ── Eval (observability; bot-scoped, fleet is @IamOnly) ──────────────────────
  listEvalRuns(p: { system: string; botId?: string; layer?: string; limit?: number }): Promise<EvalRun[]> {
    return hubFetch(
      `/api/eval/runs${qs({ system: p.system, botId: p.botId, layer: p.layer, limit: p.limit != null ? String(p.limit) : undefined })}`,
    ).then((r) => json<EvalRun[]>(r));
  },
  getEvalRunSummary(system: string, botId: string, layer?: string): Promise<EvalRunSummary> {
    return hubFetch(`/api/eval/runs/summary${qs({ system, botId, layer })}`).then((r) => json<EvalRunSummary>(r));
  },
  getEvalRun(id: string): Promise<EvalRunDetail> {
    return hubFetch(`/api/eval/runs/${encodeURIComponent(id)}`).then((r) => json<EvalRunDetail>(r));
  },
  listEvalResults(p: { system: string; botId?: string; verdict?: string; diagnosis?: string; limit?: number }): Promise<EvalResult[]> {
    return hubFetch(
      `/api/eval/results${qs({ system: p.system, botId: p.botId, verdict: p.verdict, diagnosis: p.diagnosis, limit: p.limit != null ? String(p.limit) : undefined })}`,
    ).then((r) => json<EvalResult[]>(r));
  },
  listEvalSuggestions(p: { system: string; botId?: string; status?: string; limit?: number }): Promise<EvalSuggestion[]> {
    return hubFetch(
      `/api/eval/suggestions${qs({ system: p.system, botId: p.botId, status: p.status, limit: p.limit != null ? String(p.limit) : undefined })}`,
    ).then((r) => json<EvalSuggestion[]>(r));
  },
  triggerEvalRun(dto: { system: string; botId: string; kind?: 'eval' | 'amend'; sample?: number; generation?: boolean; mode?: 'propose' | 'auto' }): Promise<{ jobId: string }> {
    return hubFetch('/api/eval/run', { method: 'POST', body: JSON.stringify(dto) }).then((r) => json<{ jobId: string }>(r));
  },
  applyEvalSuggestion(id: string, body?: { actor?: string; auto?: boolean }): Promise<EvalSuggestion> {
    return hubFetch(`/api/eval/suggestions/${encodeURIComponent(id)}/apply`, { method: 'POST', body: JSON.stringify(body ?? {}) }).then((r) =>
      json<EvalSuggestion>(r),
    );
  },
  rollbackEvalSuggestion(id: string): Promise<EvalSuggestion> {
    return hubFetch(`/api/eval/suggestions/${encodeURIComponent(id)}/rollback`, { method: 'POST', body: '{}' }).then((r) =>
      json<EvalSuggestion>(r),
    );
  },

  // ── Unrecognised comments (C-tier improvement loop) ────────────────────────
  listUnrecognizedComments(p: { system: string; botId?: string; limit?: number; offset?: number }): Promise<UnrecognizedComment[]> {
    return hubFetch(
      `/api/unrecognized-comments${qs({ system: p.system, botId: p.botId, limit: p.limit != null ? String(p.limit) : undefined, offset: p.offset != null ? String(p.offset) : undefined })}`,
    ).then((r) => json<UnrecognizedComment[]>(r));
  },
  deleteUnrecognizedComment(id: string): Promise<{ deleted: true }> {
    return hubFetch(`/api/unrecognized-comments/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) =>
      json<{ deleted: true }>(r),
    );
  },
  // ── NLU review loop (clusters + gold labels) ───────────────────────────────
  listReviewClusters(p: { system: string; botId: string; threshold?: number; limit?: number }): Promise<ReviewCluster[]> {
    return hubFetch(
      `/api/unrecognized-comments/clusters${qs({ system: p.system, botId: p.botId, threshold: p.threshold != null ? String(p.threshold) : undefined, limit: p.limit != null ? String(p.limit) : undefined })}`,
    ).then((r) => json<ReviewCluster[]>(r));
  },
  upsertGoldLabels(body: {
    system: string;
    botId: string;
    items: { text: string; labelKind: GoldLabelKind; intentCode?: string; surface?: 'comment' | 'dm' }[];
  }): Promise<{ saved: number; splits: Record<string, 'train' | 'test'> }> {
    return hubFetch(`/api/nlu/gold-labels`, {
      method: 'POST',
      body: JSON.stringify(body),
    }).then((r) => json<{ saved: number; splits: Record<string, 'train' | 'test'> }>(r));
  },
  pruneUnrecognizedComments(ttlDays?: number): Promise<{ deleted: number; ttlDays: number }> {
    return hubFetch(`/api/unrecognized-comments/prune${qs({ ttlDays: ttlDays != null ? String(ttlDays) : undefined })}`, {
      method: 'POST',
      body: '{}',
    }).then((r) => json<{ deleted: number; ttlDays: number }>(r));
  },
};

// ── Session events ─────────────────────────────────────────────────────────────
export type SessionEventKind =
  | 'route'
  | 'rag.search'
  | 'llm.generate'
  | 'tool.call'
  | 'clarification'
  | 'form.start'
  | 'form.complete'
  | 'form.cancel'
  | 'hook.deny'
  | 'error';

export interface SessionEvent {
  id: string;
  system: string;
  botId: string;
  sessionId: string | null;
  channel: string | null;
  kind: SessionEventKind | string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export const SESSION_EVENT_KINDS: SessionEventKind[] = [
  'route',
  'rag.search',
  'llm.generate',
  'tool.call',
  'clarification',
  'form.start',
  'form.complete',
  'form.cancel',
  'hook.deny',
  'error',
];

// ── Hooks ────────────────────────────────────────────────────────────────────
export type HookType = 'webhook' | 'builtin';

export interface Hook {
  id: string;
  system: string;
  botId: string;
  name: string;
  event: string;
  type: HookType;
  config: Record<string, unknown>;
  priority: number;
  enabled: boolean;
  timeoutMs: number;
  description?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HookMeta {
  events: string[];
  builtinHandlers: string[];
}

export interface CreateHookDto {
  system: string;
  botId: string;
  name: string;
  event: string;
  type: HookType;
  config: Record<string, unknown>;
  priority?: number;
  enabled?: boolean;
  timeoutMs?: number;
  description?: string;
}

export type UpdateHookDto = Partial<Omit<CreateHookDto, 'system' | 'botId'>>;

// ── Eval ─────────────────────────────────────────────────────────────────────
export interface EvalRun {
  id: string;
  system: string;
  botId: string;
  layer: string;
  status: string;
  triggeredBy?: string | null;
  actor?: string | null;
  manifest?: Record<string, unknown> | null;
  aggregate?: Record<string, unknown> | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  createdAt: string;
}

export interface EvalResult {
  id: string;
  runId: string;
  caseId?: string | null;
  system: string;
  botId: string;
  layer: string;
  input?: Record<string, unknown> | null;
  output?: Record<string, unknown> | null;
  metrics?: Record<string, unknown> | null;
  verdict?: 'pass' | 'fail' | 'partial' | null;
  score?: number | null;
  diagnosis?: string | null;
  createdAt: string;
}

export interface EvalCase {
  id: string;
  runId: string;
  system: string;
  botId: string;
  channel?: string | null;
  question: string;
  groundTruth?: Record<string, unknown> | null;
  createdAt: string;
}

export type EvalRunDetail = EvalRun & { cases: (EvalCase & { results: EvalResult[] })[] };

export interface EvalRunSummary {
  latest: EvalRun | null;
  previous: EvalRun | null;
  metric: string | null;
  delta: number | null;
  regressed: boolean | null;
}

/**
 * A comment the C-tier gate could not recognise. Mirrors the hub entity.
 *
 * `topScore` + `candidateCodes` separate the two shapes of a miss: candidates
 * present means a near miss (the phrasing belongs on an intent that already
 * exists), empty means nothing was retrieved at all (the intent is missing).
 */
export type GoldLabelKind = 'intent' | 'none' | 'noise';

export interface ReviewCluster {
  leaderId: string;
  text: string;
  totalSeen: number;
  size: number;
  topScore: number | null;
  candidateCodes: string[];
  members: { id: string; text: string; seenCount: number; surface: string | null }[];
}

export interface UnrecognizedComment {
  id: string;
  system: string;
  botId: string;
  normalizedText: string;
  text: string;
  channel?: string | null;
  seenCount: number;
  topScore?: number | null;
  candidateCodes?: string[] | null;
  firstSeenAt: string;
  lastSeenAt: string;
}

export interface EvalSuggestion {
  id: string;
  system: string;
  botId: string;
  runId?: string | null;
  caseId?: string | null;
  kind: string;
  riskClass: string;
  confidence?: number | null;
  status: 'proposed' | 'applied' | 'auto_applied' | 'rejected' | 'rolled_back' | string;
  proposal: Record<string, unknown>;
  rollbackToken?: Record<string, unknown> | null;
  appliedBy?: string | null;
  appliedAt?: string | null;
  createdAt: string;
}
